/**
 * Unified login for admin, driver, and passenger portals.
 * Users pick a portal tab first — we validate role after Supabase auth.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ForgotPasswordLink from '../components/ForgotPasswordLink';
import PasswordInput from '../components/PasswordInput';
import { useAuth } from '../authContext';
import { signInForRole } from '../lib/auth';
import { homePathForProfile } from '../lib/homePath';
import { SHOW_MARKETPLACE_UI } from '../lib/features';
import BrandMark from '../components/BrandMark';

const accent = '#0d7377';
const bg = '#0f1419';
const card = '#1a2332';
const text = '#e8eaed';
const muted = '#9aa0a6';

const tabs = [
  { id: 'admin', label: 'Organisation admin' },
  { id: 'driver', label: 'Driver' },
  { id: 'passenger', label: 'Passenger / guardian' },
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();

  const [role, setRole] = useState('passenger');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const from = location.state?.from || '/';
  const passwordResetDone = location.state?.passwordReset || searchParams.get('reset') === 'done';
  const superAdminDenied = location.state?.superAdminDenied;
  const deactivated = location.state?.deactivated;

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { profile, error: err } = await signInForRole(email, password, role);
      if (err) {
        setError(err.message || 'Sign-in failed.');
        return;
      }
      await refresh();
      let dest = homePathForProfile(profile);
      if (from && from !== '/' && from !== '/login') {
        if (from === '/super-admin' && profile?.super_admin) {
          dest = '/super-admin';
        } else if (from !== '/super-admin') {
          dest = from;
        }
      }
      navigate(dest, { replace: true });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: bg,
        color: text,
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        padding: '24px 16px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: 420, margin: '0 auto' }}>
        <header style={{ marginBottom: 28, textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
            <BrandMark size={52} />
          </div>
          <h1 style={{ fontSize: '1.75rem', fontWeight: 700, margin: '0 0 8px', letterSpacing: '-0.02em' }}>
            Transport Team
          </h1>
          <p style={{ margin: 0, color: muted, fontSize: '0.95rem' }}>
            Sign in to your organisation workspace
          </p>
          <p
            style={{
              margin: '10px 0 0',
              color: accent,
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            Free to use during early access — no payment required
          </p>
        </header>

        <div
          style={{
            background: card,
            borderRadius: 14,
            padding: 20,
            border: '1px solid rgba(255,255,255,0.06)',
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
          }}
        >
          {/* Portal selector */}
          <div
            role="tablist"
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 8,
              marginBottom: 20,
            }}
          >
            {tabs.map((t) => {
              const active = role === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setRole(t.id)}
                  style={{
                    padding: '12px 14px',
                    borderRadius: 10,
                    border: active ? `2px solid ${accent}` : '1px solid rgba(255,255,255,0.08)',
                    background: active ? 'rgba(13,115,119,0.15)' : 'rgba(0,0,0,0.2)',
                    color: text,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: active ? 600 : 500,
                    textAlign: 'left',
                  }}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <form onSubmit={handleSubmit}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: '0.85rem', color: muted }}>
              Email
            </label>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={inputStyle}
            />

            <label style={{ display: 'block', marginBottom: 6, marginTop: 14, fontSize: '0.85rem', color: muted }}>
              Password
            </label>
            <PasswordInput
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              inputStyle={inputStyle}
            />
            <ForgotPasswordLink scope="team" />

            {passwordResetDone ? (
              <p style={{ color: '#5dcaa5', fontSize: '0.9rem', marginTop: 12, marginBottom: 0 }}>
                Password updated. Sign in with your new password.
              </p>
            ) : null}

            {superAdminDenied ? (
          <p style={{ color: '#fbbc04', marginBottom: 16, fontSize: '0.9rem' }} role="status">
            Super admin access is not enabled for this account. Run the super_admins SQL in Supabase, then sign in again.
          </p>
        ) : null}
        {deactivated ? (
              <p style={{ color: '#f28b82', fontSize: '0.9rem', marginTop: 12, marginBottom: 0 }}>
                This driver account has been deactivated. Contact your organisation admin.
              </p>
            ) : null}

            {error ? (
              <p style={{ color: '#f28b82', fontSize: '0.9rem', marginTop: 12, marginBottom: 0 }}>{error}</p>
            ) : null}

            <button
              type="submit"
              disabled={busy}
              style={{
                marginTop: 20,
                width: '100%',
                padding: '14px 16px',
                borderRadius: 10,
                border: 'none',
                background: `linear-gradient(180deg, ${accent}, #0a5c5f)`,
                color: '#fff',
                fontWeight: 600,
                fontSize: '1rem',
                cursor: busy ? 'wait' : 'pointer',
                opacity: busy ? 0.85 : 1,
              }}
            >
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p style={{ marginTop: 20, marginBottom: 0, fontSize: '0.88rem', color: muted, textAlign: 'center' }}>
            New passenger or driver?{' '}
            <span style={{ color: text }}>
              Use your organisation&apos;s registration link (includes the org slug).
            </span>
          </p>
          <p style={{ marginTop: 16, textAlign: 'center', fontSize: '0.88rem' }}>
            <Link to="/org/register" style={{ color: accent, textDecoration: 'none', fontWeight: 600 }}>
              Register a new organisation
            </Link>
          </p>
          {SHOW_MARKETPLACE_UI ? (
            <p style={{ marginTop: 12, textAlign: 'center' }}>
              <Link to="/marketplace/login" style={{ color: accent, fontSize: '0.85rem', textDecoration: 'none' }}>
                Driver booking sign in
              </Link>
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '12px 14px',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.12)',
  background: '#0f1419',
  color: text,
  fontSize: '1rem',
  outline: 'none',
};
